import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { apiError } from "../../shared/api/client";
import { platformApi, platformQueryKeys } from "../../shared/api/platform";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** SaaS platform console — provision a new clinic (tenant) + its owner account. ADR-0021. */
export function PlatformTenantCreatePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [clinicName, setClinicName] = useState("");
  const [code, setCode] = useState("");
  const [timezone, setTimezone] = useState("Asia/Ho_Chi_Minh");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!clinicName.trim() || !code.trim() || !ownerEmail.trim() || !ownerPassword) {
      setError("Vui lòng nhập đầy đủ các trường bắt buộc.");
      return;
    }
    setSubmitting(true);
    try {
      await platformApi.createTenant({
        clinicName: clinicName.trim(),
        code: code.trim().toUpperCase(),
        timezone: timezone.trim() || "Asia/Ho_Chi_Minh",
        ownerEmail: ownerEmail.trim(),
        ownerPassword,
      });
      await qc.invalidateQueries({ queryKey: platformQueryKeys.tenants() });
      navigate("/platform/tenants");
    } catch (err) {
      setError(apiError(err).detail);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">
          Tạo phòng khám
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Tạo một phòng khám mới và tài khoản chủ phòng khám đầu tiên.
        </p>
      </div>

      <Card className="max-w-xl">
        <CardContent className="p-4 sm:p-6">
          <form className="space-y-5" onSubmit={onSubmit} noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="clinicName">Tên phòng khám *</Label>
              <Input
                id="clinicName"
                type="text"
                value={clinicName}
                onChange={(e) => setClinicName(e.target.value)}
                maxLength={256}
                placeholder="VD: Phòng khám Lương Ngọc"
                disabled={submitting}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="code">Mã phòng khám *</Label>
              <Input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                maxLength={64}
                placeholder="VD: LUONGNGOC (chữ HOA, số, gạch ngang)"
                disabled={submitting}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="timezone">Múi giờ</Label>
              <Input
                id="timezone"
                type="text"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                maxLength={64}
                disabled={submitting}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ownerEmail">Email chủ phòng khám *</Label>
              <Input
                id="ownerEmail"
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                maxLength={256}
                placeholder="VD: admin@luongngoc.vn"
                autoComplete="off"
                disabled={submitting}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ownerPassword">Mật khẩu chủ phòng khám *</Label>
              <Input
                id="ownerPassword"
                type="password"
                value={ownerPassword}
                onChange={(e) => setOwnerPassword(e.target.value)}
                minLength={8}
                maxLength={256}
                placeholder="Tối thiểu 8 ký tự, không nằm trong danh sách rò rỉ"
                autoComplete="new-password"
                disabled={submitting}
              />
            </div>

            {error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate("/platform/tenants")}
                disabled={submitting}
              >
                Huỷ
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? "Đang tạo…" : "Tạo phòng khám"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
